import React, { useState, useEffect } from 'react';
import { Users, UserPlus, Trash2, Shield, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('at least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('one number');
    return errors;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (newUser.password !== newUser.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const passwordErrors = validatePassword(newUser.password);
    if (passwordErrors.length > 0) {
      toast.error(`Password must have: ${passwordErrors.join(', ')}`);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/users`, {
        username: newUser.username,
        password: newUser.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Support admin user created successfully!');
      setNewUser({ username: '', password: '', confirmPassword: '' });
      setShowAddForm(false);
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '1.75rem', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Users size={28} />
          Admin Users ({users.length})
        </h2>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.5rem'
          }}
        >
          <UserPlus size={20} />
          Add Support Admin
        </button>
      </div>

      {/* Add User Form */}
      {showAddForm && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '15px',
          border: '2px solid #d4a574',
          marginBottom: '2rem'
        }}>
          <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#2c3e50' }}>
            Create New Support Admin
          </h3>
          <form onSubmit={handleAddUser}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Username *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                  placeholder="Enter username"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #f0f0f0',
                    borderRadius: '8px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Password *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  required
                  placeholder="Min 8 chars, 1 uppercase, 1 lowercase, 1 number"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #f0f0f0',
                    borderRadius: '8px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={newUser.confirmPassword}
                  onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                  required
                  placeholder="Re-enter password"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #f0f0f0',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="submit"
                className="btn-primary"
                style={{ padding: '0.75rem 2rem' }}
              >
                Create Admin User
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewUser({ username: '', password: '', confirmPassword: '' });
                }}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'white',
                  border: '2px solid #f0f0f0',
                  borderRadius: '50px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div style={{ display: 'grid', gap: '1rem' }}>
        {users.map((user) => (
          <div
            key={user.id}
            style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '15px',
              border: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: user.role === 'primary' ? 'linear-gradient(135deg, #d4a574, #c89b64)' : '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: user.role === 'primary' ? 'white' : '#5a6c7d'
              }}>
                {user.role === 'primary' ? <Shield size={28} /> : <UserIcon size={28} />}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                  <h3 style={{ fontSize: '1.25rem', color: '#2c3e50', margin: 0 }}>
                    {user.username}
                  </h3>
                  <span style={{
                    padding: '0.25rem 0.75rem',
                    background: user.role === 'primary' ? 'linear-gradient(135deg, #d4a574, #c89b64)' : 'rgba(59, 130, 246, 0.1)',
                    color: user.role === 'primary' ? 'white' : '#2563eb',
                    borderRadius: '50px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {user.role === 'primary' ? 'Primary Admin' : 'Support Admin'}
                  </span>
                </div>
                <div style={{ fontSize: '0.875rem', color: '#5a6c7d' }}>
                  Created: {new Date(user.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div>
              {user.role !== 'primary' && (
                <button
                  onClick={() => handleDeleteUser(user.id, user.username)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Trash2 size={18} />
                  Delete
                </button>
              )}
              {user.role === 'primary' && (
                <span style={{
                  padding: '0.75rem 1.5rem',
                  background: '#f0f0f0',
                  color: '#5a6c7d',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '0.875rem'
                }}>
                  Cannot be deleted
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {users.length === 1 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#5a6c7d',
          background: 'white',
          borderRadius: '15px',
          marginTop: '2rem'
        }}>
          <Users size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p>No support admin users yet. Click "Add Support Admin" to create one.</p>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
